import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronLeft, ChevronRight, Images, Mic, Play, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-color.png`;
const LOGO_WHITE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-white.png`;

const TABS = [["photos", "Photos"], ["videos", "Videos"], ["interviews", "Interviews"]];

export const BUILTIN_PHOTOS = [
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_kpl-league/artifacts/8426f2b0d00fb163_KPL%20PRESS%20MEET%203.webp", title: "KPL press meet — Season One unveiling" },
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_kpl-league/artifacts/163de884bb3f74dc_KPL%20PRESS%20MEET%202.webp", title: "KPL press meet — T-20 2026-27 launch" },
  { src: "https://customer-assets-jai6qajn.emergentagent.net/job_kpl-league/artifacts/754516e55dbbcf5a_image.png", title: "RMG ground — a grassroots movement" },
  { src: `${BACKEND_URL}/api/static/kpl-insta-auction.png`, title: "Player auction — 25 Nov 2026, Svenska Design Hotel" },
  { src: `${BACKEND_URL}/api/static/kpl-insta-talent.png`, title: "Andhra's talent. Kakinada's stage." },
];

const VideoGrid = ({ items, emptyIcon, emptyTitle, emptyText, testid }) => (
  items.length === 0 ? <div className="gallery-empty" data-testid={`${testid}-empty`}>
    {emptyIcon}
    <h3>{emptyTitle}</h3>
    <p>{emptyText}</p>
  </div> : <div className="video-grid" data-testid={testid}>
    {items.map((item, index) => <figure className="video-card" key={item.id} data-testid={`gallery-video-${index}`}>
      <video controls preload="metadata" src={`${API}/gallery/file/${item.storage_path}`} />
      <figcaption>{item.title}</figcaption>
    </figure>)}
  </div>
);

export default function Gallery() {
  const { tab } = useParams();
  const active = TABS.some(([key]) => key === tab) ? tab : "photos";
  const [items, setItems] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [touchX, setTouchX] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  const photos = [...BUILTIN_PHOTOS, ...items.filter((i) => i.kind === "photo").map((i) => ({ src: `${API}/gallery/file/${i.storage_path}`, title: i.title }))];
  const videos = items.filter((i) => i.kind === "video");
  const interviews = items.filter((i) => i.kind === "interview");

  const step = useCallback((delta) => setLightbox((current) => (current === null ? null : (current + delta + photos.length) % photos.length)), [photos.length]);

  useEffect(() => {
    if (lightbox === null) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setLightbox(null);
      if (event.key === "ArrowLeft") step(-1);
      if (event.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, step]);

  const onTouchEnd = (event) => {
    if (touchX === null) return;
    const delta = event.changedTouches[0].clientX - touchX;
    if (Math.abs(delta) > 40) step(delta > 0 ? -1 : 1);
    setTouchX(null);
  };

  return <div className="gallery-wrap">
    <div className="container">
      <div className="gallery-top">
        <img className="auth-logo gallery-logo" src={LOGO_URL} alt="KPL logo" data-testid="gallery-logo" />
        <Link className="auth-home gallery-back" to="/" data-testid="gallery-back-home"><ArrowLeft size={15} /> BACK TO SITE</Link>
      </div>
      <div className="gallery-head">
        <p className="eyebrow blue">KPL SEASON ONE</p>
        <h1 className="gallery-title">Gallery.</h1>
      </div>
      <div className="gallery-tabs" data-testid="gallery-tabs">
        {TABS.map(([key, label]) => <Link key={key} className={`gallery-tab ${active === key ? "active" : ""}`} to={`/gallery/${key}`} data-testid={`gallery-tab-${key}`}>{label}</Link>)}
      </div>

      {active === "photos" && <div className="photo-grid" data-testid="gallery-photos-grid">
        {photos.map((photo, index) => <figure className="photo-card" key={photo.src} data-testid={`gallery-photo-${index}`} onClick={() => setLightbox(index)}>
          <img src={photo.src} alt={photo.title} loading="lazy" />
          <figcaption>{photo.title}</figcaption>
        </figure>)}
      </div>}

      {active === "videos" && <VideoGrid items={videos} testid="gallery-videos" emptyIcon={<Play size={40} />} emptyTitle="Videos coming soon" emptyText={<>Match highlights, auction moments and behind-the-scenes clips will appear here once the season begins. Follow <a href="https://www.instagram.com/kakinadapremierleague/" target="_blank" rel="noreferrer">@kakinadapremierleague</a> for the latest videos.</>} />}

      {active === "interviews" && <VideoGrid items={interviews} testid="gallery-interviews" emptyIcon={<Mic size={40} />} emptyTitle="Interviews coming soon" emptyText="Conversations with franchise owners, captains and KPL organisers will be published here. Stay tuned." />}

      {active === "photos" && <p className="gallery-more" data-testid="gallery-photos-note"><Images size={14} /> Tap any photo to view it full-screen. More photos from the auction and Season One matches will be added here.</p>}
    </div>

    {lightbox !== null && <div className="lightbox" data-testid="lightbox" onClick={() => setLightbox(null)} onTouchStart={(e) => setTouchX(e.changedTouches[0].clientX)} onTouchEnd={onTouchEnd}>
      <button className="lightbox-close" onClick={() => setLightbox(null)} data-testid="lightbox-close" aria-label="Close"><X size={22} /></button>
      <button className="lightbox-arrow prev" onClick={(e) => { e.stopPropagation(); step(-1); }} data-testid="lightbox-prev" aria-label="Previous photo"><ChevronLeft size={26} /></button>
      <figure className="lightbox-stage" onClick={(e) => e.stopPropagation()}>
        <img src={photos[lightbox].src} alt={photos[lightbox].title} data-testid="lightbox-image" />
        <figcaption data-testid="lightbox-caption">{photos[lightbox].title} · {lightbox + 1} / {photos.length}</figcaption>
      </figure>
      <button className="lightbox-arrow next" onClick={(e) => { e.stopPropagation(); step(1); }} data-testid="lightbox-next" aria-label="Next photo"><ChevronRight size={26} /></button>
    </div>}
  </div>;
}
